// src/components/DetailsTab.js
import React, { useEffect, useState, forwardRef, useImperativeHandle, useId, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import OrganizationSelect from '../../../components/OrganizationSelect';
import rightArrowIcon from '../../../assets/img/right-arrow.svg';
import {
  cleanFormattedPricing,
  formatPricing,
  FrequencyOptions,
  getDateFormat,
  orgNameLimit,
  priceLimit,
  selectLoader,
  todayStart,
  validateDetailsTabForm,
  validPricing,
  weeklyDaysOptions
} from '../../../utils/Helpers';
import APIServices from '../../../services/APIServices';
import appToast, { TOAST_TYPE } from '../../../utils/AppToast';
import { useParams } from 'react-router-dom';
import { useStore } from '../../../hooks/useStore';
import { useAuth } from 'contexts/AuthContext';
import timeUp from '../../../assets/img/time-up-arro.svg';
import timeDown from '../../../assets/img/time-doun-arro.svg';
import RecurrenceForm from '../forms/RecurrenceForm';
import { fetchDateTypes, fetchFrequencyOptions } from 'utils/fetchData';

const DetailsTab = forwardRef(
  (
    {
      isInternalProject,
      addedOrgId,
      openModal,
      setCurrentTab,
      setCompletedTabs,
      setprojectDetails,
      organizationsList,
      orgloading,
      setProjectStatus,
      setEditProjectDetails,
      setInternalProject,
      isModal = false,
      initialProjectData = null,
      prefetchedProjectDetails = null,
      milestones = [],
      setProjectDetails = () => {}
    },
    ref
  ) => {
    const recurringId = useId();
    const internalId = useId();
    const { userCompany } = useAuth();
    const { projectId: projectIdFromParams } = useParams();
    const [store] = useStore();
    const submissionInProgressRef = useRef(false);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);
    const [error, setError] = useState(null);
    const [details, setDetails] = useState('');
    const [detailsId, setDetailsId] = useState('');
    const [frequencyOptions, setFrequencyOptions] = useState([]);
    const [dateTypes, setDateTypes] = useState([]);
    const [initialData, setInitialData] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [formValues, setFormValues] = useState({
      isRecurring: false,
      projectName: '',
      organizationId: '',
      startDate: null,
      endDate: null,
      estimatedPrice: '',
      type: 'internal'
    });
    const [isEditing, setEditing] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
      recurrenceFrequency: 'weekly',
      recurrenceStartDate: null,
      isAutoRenew: false,
      dateType: 'ongoing',
      recurrenceCycleCount: 0
    });

    useImperativeHandle(ref, () => ({
      submitDetails: handleSubmit
    }));

    useEffect(() => {
      if (projectIdFromParams) {
        console.log(projectIdFromParams);
        setEditing(true);

        if (prefetchedProjectDetails?._id === projectIdFromParams) {
          setDetails(prefetchedProjectDetails);
          setEditProjectDetails(prefetchedProjectDetails);
          setDetailsId(prefetchedProjectDetails._id);
        } else {
          fetchProjectDetails(projectIdFromParams);
        }
      }
    }, [projectIdFromParams, prefetchedProjectDetails]);

    useEffect(() => {
      if (!initialProjectData || isInitialized) return;
      console.log(initialProjectData, 'initialProjectData in details tab');

      setFormValues((prev) => ({
        ...prev,
        projectName: initialProjectData.projectName || '',
        startDate: initialProjectData.startDate ? new Date(initialProjectData.startDate) : null,
        endDate: initialProjectData.endDate ? new Date(initialProjectData.endDate) : null,
        estimatedPrice: initialProjectData.estimatedValue || '',
        organizationId: initialProjectData.organizationId || '',
        isRecurring: initialProjectData.isRecurring || false
      }));
      setIsInitialized(true);
    }, [initialProjectData]);

    useEffect(() => {
      if (!isModal) return;

      setProjectDetails((prev) => ({
        ...(prev || {}),

        projectName: formValues.projectName,
        organizationId: formValues.organizationId || null,
        startDate: formValues.startDate,
        endDate: formValues.endDate,
        estimatedValue: Number(formValues.estimatedPrice) || 0,
        isRecurring: formValues.isRecurring
      }));
    }, [formValues]);

    useEffect(() => {
      if (!isModal) return;

      if (!formValues.isRecurring) {
        setProjectDetails((prev) => {
          const { recurrenceFrequency, recurrenceStartDate, dateType, recurrenceCycleCount, isAutoRenew, ...rest } =
            prev;

          return {
            ...rest,
            isRecurring: false
          };
        });
        return;
      }

      setProjectDetails((prev) => ({
        ...prev,
        isRecurring: true,
        recurrenceFrequency: formData.recurrenceFrequency,
        recurrenceStartDate: formData.recurrenceStartDate,
        dateType: formData.dateType,
        recurrenceCycleCount: formData.recurrenceCycleCount,
        isAutoRenew: formData.isAutoRenew
      }));
    }, [formValues.isRecurring, formData]);

    useEffect(() => {
      fetchFrequencyOptions(setFrequencyOptions);
      fetchDateTypes(setDateTypes);
    }, []);

    useEffect(() => {
      if (!details) return;
      if (details.type === 'internal') {
        setInternalProject(true);
      }
      const { projectName, organizationId, startDate, endDate, estimatedPrice, isRecurring } = details;

      // Set organizationId to null if it's an internal project
      const orgId = details.type === 'internal' ? null : organizationId?._id;

      // setEditing(true);
      setFormValues((prevValues) => ({
        ...prevValues,
        projectName,
        organizationId: orgId,
        startDate,
        endDate,
        estimatedPrice,
        isRecurring
      }));

      setCompletedTabs((prev) => ({
        ...prev,
        2: true, // Enable the next tabs
        3: true,
        4: true,
        5: true
      }));
    }, [details]);

    useEffect(() => {
      if (!details) return;

      const { isRecurring, recurrenceFrequency, recurrenceStartDate, dateType, recurrenceCycleCount, isAutoRenew } =
        details;

      setInitialData({
        isRecurring,
        recurrenceFrequency: frequencyOptions.find((x) => x._id === recurrenceFrequency)?.value,
        recurrenceStartDate,
        recurrenceCycleCount,
        isAutoRenew,
        dateType: dateTypes.find((x) => x._id === dateType)?.value
      });
    }, [details, dateTypes, frequencyOptions]);

    const fetchProjectDetails = async (projectId) => {
      setPageLoading(true);
      try {
        let response = await APIServices.get(`/project/${projectId}`);
        console.log(response);
        if (response.projectDetail) {
          setDetails(response.projectDetail);
          setEditProjectDetails(response.projectDetail);
          setDetailsId(response?.projectDetail?._id);
        }
      } catch (error) {
        console.error('Error fetching Project details:', error);
      } finally {
        setPageLoading(false);
      }
    };

    const handleChange = (e) => {
      const { name, value, checked } = e.target;

      switch (name) {
        case 'isInternalProject':
          setInternalProject(checked);
          break;

        case 'isRecurring':
          setFormValues((prev) => ({
            ...prev,
            [name]: checked
          }));
          setErrors({});
          break;

        case 'estimatedPrice':
          const raw = value.replace(/[^0-9]/g, ''); // ✅ only digits

          setFormValues((prev) => ({
            ...prev,
            estimatedPrice: raw
          }));
          break;

        default:
          setFormValues((prev) => ({
            ...prev,
            [name]: value
          }));
          break;
      }

      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: ''
        }));
      }
    };

    const handleDateChange = (name, date) => {
      if (name === 'endDate' && date && isModal) {
        // Access milestones from parent (we need to pass them)
        if (milestones?.length) {
          const invalid = milestones.some((m) => {
            if (!m.dueDate) return false;

            return new Date(m.dueDate) > new Date(date);
          });

          if (invalid) {
            appToast('Project end date cannot be before milestone due dates.', TOAST_TYPE.ERROR);
            return; // ❌ block selection
          }
        }
      }

      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: date
      }));

      // Remove errors dynamically when the user enters valid data
      if (errors[name]) {
        setErrors({
          ...errors,
          [name]: ''
        });
      }
    };

    const handleSubmit = async (event) => {
      event?.preventDefault?.();

      if (submissionInProgressRef.current) {
        return null;
      }

      let formattedFormdata = getFilteredRecurrencePayload(formData);
      console.log(formattedFormdata);

      // e.preventDefault();
      const endpoint = isEditing ? `/project/${detailsId}` : '/project';

      if (!validateDetailsTabForm(formValues, formattedFormdata, setErrors, isInternalProject)) {
        submissionInProgressRef.current = true;
        setLoading(true);
        // Format the dates to MM/DD/YYYY
        const formattedStartDate = formValues.startDate
          ? new Date(formValues.startDate).toLocaleDateString('en-US')
          : null;
        const formattedDueDate = formValues.endDate ? new Date(formValues.endDate).toLocaleDateString('en-US') : null;

        let formattedFormData = {
          ...formValues,
          ...(formValues.isRecurring ? formattedFormdata : {}),
          startDate: formValues.isRecurring ? formattedFormdata.recurrenceStartDate : formattedStartDate,
          endDate: formattedDueDate,
          estimatedPrice: Number(cleanFormattedPricing(formValues.estimatedPrice)),
          type: isInternalProject ? 'internal' : 'external'
        };

        if (isInternalProject) {
          formattedFormData = {
            ...(formValues.isRecurring ? formattedFormdata : {}),
            ...formValues,
            startDate: formValues.isRecurring ? formattedFormdata.recurrenceStartDate : formattedStartDate,
            endDate: formattedDueDate,
            organizationId: null,
            companyId: userCompany?._id
          };
        }

        if (formValues.isRecurring === true) {
          // Destructure to remove endDate
          const { endDate, ...rest } = formattedFormData;
          formattedFormData = rest;
        }

        try {
          const response = isEditing
            ? await APIServices.put(endpoint, formattedFormData)
            : await APIServices.post(endpoint, formattedFormData);

          if (response.project) {
            let projectDetails = response.project;

            setEditing(true);
            setDetailsId(projectDetails._id);

            setprojectDetails?.(projectDetails);
            setProjectStatus?.('inProgress');

            // Only run tab navigation in normal page flow
            if (!isModal) {
              setCurrentTab?.(2);
              setCompletedTabs((prev) => ({
                ...prev,
                [2]: true
              }));
            }

            return projectDetails; // important for modal flow
          } else {
            appToast(response.data.message, TOAST_TYPE.ERROR);
          }
        } catch (error) {
          setError(error.message);
        } finally {
          submissionInProgressRef.current = false;
          setLoading(false);
        }
      } else {
        console.log(errors);
      }
      return null;
    };
    const getStartOfDay = () => {
      if (store?.startCalenderWeek?.toLowerCase() == 'monday') {
        return 1;
      } else {
        return 0;
      }
    };

    const isFutureDate = (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset today's time to midnight for comparison

      // Ensure `date` is a Date object
      const inputDate = new Date(date);

      return inputDate > today;
    };

    const getFilteredRecurrencePayload = (data) => {
      const { recurrenceFrequency, recurrenceStartDate, dateType, isAutoRenew, recurrenceCycleCount } = data;

      const payload = {
        isAutoRenew,
        recurrenceStartDate
      };

      payload.recurrenceFrequency = frequencyOptions.find((x) => x.value === recurrenceFrequency)?._id;
      payload.dateType = dateTypes.find((x) => x.value === dateType)?._id;
      payload.recurrenceStartDate = recurrenceStartDate
        ? new Date(recurrenceStartDate).toLocaleDateString('en-US')
        : null;
      if (dateType === 'cycles') {
        payload.recurrenceCycleCount = recurrenceCycleCount;
      }

      return payload;
    };

    if (error) {
      return <div>Error: {error}</div>;
    }

    return (
      <div className="tabCover-container">
        {pageLoading ? (
          <div className="nodata-loader">{selectLoader(70)}</div>
        ) : (
          <div className="details-tab">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* left colom */}
                <div className="col-lg-6 col-md-6">
                  <div className="head-title-text">Project Name</div>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="input-group">
                        <input
                          maxLength={orgNameLimit}
                          type="text"
                          className={`form-control ${errors.projectName ? 'form-invalid' : ''}`}
                          placeholder="Project Name"
                          name="projectName"
                          value={formValues.projectName}
                          onChange={handleChange}
                        />
                        {errors.projectName && <div className="error-message">{errors.projectName}</div>}
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="input-group">
                        <div className={` internal-switch ${isEditing ? 'input-desable' : ''}`}>
                          <h4>Recurring Project</h4>
                          <div className="form-switch">
                            <input
                              type="checkbox"
                              checked={formValues.isRecurring}
                              onChange={handleChange}
                              name="isRecurring"
                              hidden="hidden"
                              id={recurringId}
                              disabled={isEditing}
                            />
                            <label className="switch" htmlFor={recurringId}></label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {formValues.isRecurring ? (
                    <RecurrenceForm
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                      setErrors={setErrors}
                      frequencyOptions={frequencyOptions}
                      dateTypes={dateTypes}
                      initialData={initialData}
                      isEditing={isEditing}
                      projectIdFromParams={projectIdFromParams}
                      showleftPart={true}
                    />
                  ) : (
                    <>
                      <div className={`radio_btn_section br-10 px-4 py-3 ${errors.startDate ? 'form-invalid' : ''}`}>
                        <div className="radio_inner_sec align-items-center">
                          <div className="text-left">
                            <label htmlFor="startDate" className="label-title">
                              Project Start
                            </label>
                          </div>
                          <DatePicker
                            disabled={projectIdFromParams ? !isFutureDate(formValues.startDate) : false}
                            selected={formValues.startDate}
                            onChange={(date) => handleDateChange('startDate', date)}
                            className="datepicker-days border-0"
                            placeholderText="+ Select Date"
                            dateFormat={getDateFormat(store.currentDateFormat)}
                            // minDate={todayStart()}
                            onKeyDown={(e) => e.preventDefault()}
                            calendarStartDay={getStartOfDay()}
                            popperPlacement="top-start"
                            popperProps={{
                              strategy: 'fixed'
                            }}
                          />
                        </div>
                      </div>
                      {errors.startDate && (
                        <div className="error-message mb-1" style={{ left: '12px', bottom: '0' }}>
                          {errors.startDate}
                        </div>
                      )}
                    </>
                  )}
                  <div className="row">
                    <div className="col-lg-12">
                      <div className={`form-control form-control-padd  ${errors.estimatedPrice ? 'form-invalid' : ''}`}>
                        <div className="row align-items-center estimated-value ">
                          <div className="col label-title text-left">Estimated Value</div>
                          <div className="col price-for-project">
                            <div className="form-group mb-0">
                              <input
                                maxLength={priceLimit}
                                type="text"
                                id="typeNumber"
                                className="project-price"
                                placeholder="$ 0.00"
                                name="estimatedPrice"
                                value={
                                  formValues.estimatedPrice && `$ ${Number(formValues.estimatedPrice).toLocaleString()}`
                                }
                                onChange={handleChange}
                              />
                            </div>
                          </div>
                        </div>
                        {errors.estimatedPrice && (
                          <div className="error-message" style={{ left: '12px' }}>
                            {errors.estimatedPrice}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* right colom */}
                <div className="col-lg-6 col-md-6">
                  <div className="head-title-text">Client Name</div>
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="input-group">
                        <div className={` internal-switch ${isEditing ? 'input-desable' : ''}`}>
                          <h4>Internal Project</h4>
                          <div className="form-switch">
                            <input
                              type="checkbox"
                              checked={isInternalProject}
                              onChange={handleChange}
                              name="isInternalProject"
                              hidden="hidden"
                              id={internalId}
                              disabled={isEditing}
                            />
                            <label className="switch" htmlFor={internalId}></label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* {!isInternalProject && ( */}
                  <div className="row">
                    <div className="col-lg-12">
                      <OrganizationSelect
                        addedOrgId={addedOrgId}
                        formValues={formValues}
                        handleChange={handleChange}
                        openModal={openModal}
                        errors={errors}
                        organizationsList={isInternalProject ? [userCompany] : organizationsList}
                        orgloading={orgloading}
                        isEditing={isEditing}
                        isInternalProject={isInternalProject}
                        showAddOrg={!isModal}
                      />
                    </div>
                  </div>
                  {/* )} */}
                  {formValues.isRecurring ? (
                    <RecurrenceForm
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                      setErrors={setErrors}
                      frequencyOptions={frequencyOptions}
                      dateTypes={dateTypes}
                      initialData={initialData}
                      isEditing={isEditing}
                      projectIdFromParams={projectIdFromParams}
                    />
                  ) : (
                    <div className="row">
                      <div className="col-lg-12">
                        <div className={`radio_btn_section br-10 px-4 py-3 ${errors.endDate ? 'form-invalid' : ''}`}>
                          <div className="radio_inner_sec align-items-center">
                            <div className="text-left">
                              <label htmlFor="dueDate" className="label-title">
                                Project Due
                              </label>
                            </div>
                            <DatePicker
                              disabled={projectIdFromParams ? !isFutureDate(formValues.startDate) : false}
                              selected={formValues.endDate}
                              onChange={(date) => handleDateChange('endDate', date)}
                              className="datepicker-days border-0"
                              placeholderText="+ Select Date"
                              // dateFormat="MMMM d, yyyy"
                              dateFormat={getDateFormat(store.currentDateFormat)}
                              minDate={formValues.startDate || todayStart()}
                              onKeyDown={(e) => e.preventDefault()}
                              calendarStartDay={getStartOfDay()}
                              popperPlacement="top-start"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                            />
                          </div>
                        </div>
                        {errors.endDate && (
                          <div className="error-message mb-1" style={{ left: '12px', bottom: '0' }}>
                            {errors.endDate}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}
        {!isModal && !pageLoading && (
          <div className="btn-black-cover mb-2">
            <button className="btn-black" type="submit" onClick={handleSubmit} disabled={loading} aria-busy={loading}>
              {!loading ? (isEditing ? 'Update' : 'Continue') : selectLoader(20)}
              {!loading && <img src={rightArrowIcon} alt="comp1" className="rightArrowIcon-arrow" />}
            </button>
          </div>
        )}
      </div>
    );
  }
);

export default DetailsTab;
